import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from '../../../environments/environment.dev';
import { map, Observable } from "rxjs";
import { BaseService } from "../../@core/services/base.service";
import { IRequestAccess, IUser, ResponseJson, SHARE_ACCESS, SHARE_FEATURE } from "../../@core/models/model";
import { USER_ROLE, USER_TYPE } from "../../@core/models/enums";
import { AuthenticationService, ILogin } from "../../@core/services/authentication.service";
import { INotificationType } from "./notification.service";


@Injectable({ providedIn: 'root' })
export class UserService extends BaseService {

    constructor(
        protected override http: HttpClient,
        private authService: AuthenticationService
    ) {
        super(http);
    }

    usersUrl = environment.apiUrl + '/user'

    getUsers() {
        return this.get<{ users: IUser[], total: number, activeCount: number }>(this.usersUrl + '/list')
    }

    getAdmins() {
        return this.get<IUser[]>(this.usersUrl + '/admins')
    }

    getProjectUsers(projectId: string) {
        return this.get<IUser[]>(this.usersUrl + '/project-users', { projectId });
    }

    getCurrentUser(): Observable<ResponseJson<IUser>> {
        return this.get<IUser>(this.usersUrl + '/profile');
    }

    updateWalkThrough(data: { type: string, status: boolean }) {
        return this.post<any>(this.usersUrl + '/update-product-walkthrough', data).pipe(map(data => {
            return this.authService.setUserData(data.data)
        }))
    }
    updateProfile(userData: FormData): Observable<ResponseJson<{ user: IUser, token: ILogin['token'] }>> {
        return this.put<{ user: IUser, token: ILogin['token'] }>(this.usersUrl + '/profile', userData).pipe(
            map(res => {
                if (res.success) {
                    const { user, token } = res.data;
                    this.authService.setUserData({ token });
                }
                return res;
            })
        );
    }

    generateMfaQR(): Observable<ResponseJson<{ qrCode: string, secret: string }>> {
        return this.get<{ qrCode: string, secret: string }>(this.usersUrl + '/mfa/generate-qr');
    }

    inviteUser(data: {
        email: string,
        role: USER_ROLE
    }[]) {
        return this.post(this.usersUrl + '/invite', data)
    }

    deleteUser(userId: string) {
        return this.delete(this.usersUrl + '/delete/' + userId)
    }

    updateUser(user: Partial<IUser>) {
        return this.put<{ token: ILogin['token'] }>(this.usersUrl + '/', { user })
    }

    changePassword(formData: FormData) {
        return this.put<any>(this.usersUrl + '/change-password', formData).pipe(
            map(res => {
                if (res.success) {
                    const { user, token } = res.data;
                    this.authService.setUserData({ token });
                }
                return res;
            }))
    }

    getUserDetailsByEmail(email: string) {
        return this.get<{
            user: IUser,
            type: USER_TYPE
        }>(this.usersUrl + '/user-details-by-email', { email })
    }


    getWorkspaceUsers(filters?: { role?: SHARE_ACCESS[], includeAdmins?: boolean }) {
        return this.post<IUser[]>(this.usersUrl + '/workspace-users', { filters })
    }

    getAccessRequests() {
        return this.get<IRequestAccess[]>(this.usersUrl + '/access-requests')
    }

    getAssignees(resource: string) {
        return this.get<IUser[]>(this.usersUrl + '/assignees', { resource })
    }

    getAllActiveUsersAndInvitesCount() {
        return this.get<{ totalActiveUsers: number, totalActiveInvites: number }>(this.usersUrl + '/active-users-and-invites-count')
    }

    getUserSettings() {
        return this.get<{ notificationTypes: INotificationType[] }>(this.usersUrl + '/user-settings');
    }

    updateUserSettings(settings: { notificationTypes: INotificationType[] }) {
        return this.put(this.usersUrl + '/user-settings', settings);
    }

    updateUserAgentModeSettings(userId: string, agentModeSettings: { askMode: boolean; agentMode: boolean }) {
        return this.put<{ agentModeSettings: { askMode: boolean; agentMode: boolean } }>(this.usersUrl + `/agent-mode-settings/${userId}`, { agentModeSettings });
    }
    transferBillingAdmin(userId: string) {
        return this.post(this.usersUrl + '/transfer-billing-admin', { userId });
    }

    transferSystemAdmin(userId: string) {
        return this.post(this.usersUrl + '/transfer-system-admin', { userId });
    }
}

